/* ==========================================================================
   Sevine Dumont: collection page (agent C)
   <sd-collection-grid>: material filter (All / Suede / Grain), family filter
   (?family=houle, from the family icons), view switch, sort.
   <sd-family-nav>: filters in place when the grid on this page has that family.
   Vanilla, no dependencies. Works without JS: links and the sort still load.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('sd-collection-grid')) return;

  var store = {
    get: function (key) {
      try { return window.localStorage.getItem(key); } catch (e) { return null; }
    },
    set: function (key, value) {
      try { window.localStorage.setItem(key, value); } catch (e) { /* storage blocked */ }
    }
  };

  function params() {
    try { return new URL(window.location.href).searchParams; } catch (e) { return null; }
  }

  function clean(value) {
    return (value || '').toString().toLowerCase().trim().replace(/^famil(ie|y)-/, '');
  }

  function titleCase(value) {
    return value.replace(/(^|[\s-])\S/g, function (s) { return s.toUpperCase(); });
  }

  function activeGrid() {
    return document.querySelector('sd-collection-grid');
  }

  class SdCollectionGrid extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.grid = this.querySelector('[data-sd-grid]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-sd-item]'));
      this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-sd-tile]'));
      this.materialButtons = Array.prototype.slice.call(this.querySelectorAll('[data-sd-material]'));
      this.viewButtons = Array.prototype.slice.call(this.querySelectorAll('[data-sd-view]'));
      this.viewMobileButtons = Array.prototype.slice.call(this.querySelectorAll('[data-sd-view-mobile]'));
      this.countEl = this.querySelector('[data-sd-count]');
      this.emptyEl = this.querySelector('[data-sd-filter-empty]');
      this.activeEl = this.querySelector('[data-sd-active]');
      this.activeLabel = this.querySelector('[data-sd-active-label]');
      this.sortEl = this.querySelector('[data-sd-sort]');

      var p = params();
      this.material = p ? clean(p.get('material')) : '';
      this.family = p ? clean(p.get('family')) : '';
      if (this.material && !this.materialButtons.length) this.material = '';
      if (this.family && !this.hasFamily(this.family)) this.family = '';

      this.bind();
      this.restoreView();
      this.apply(false);
    }

    bind() {
      var self = this;

      this.materialButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.material = btn.getAttribute('data-sd-material') || '';
          self.apply(true);
        });
      });

      this.viewButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var cols = btn.getAttribute('data-sd-view');
          self.setView('desktop', cols);
          store.set('sd-cgrid-view', cols);
        });
      });

      this.viewMobileButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var cols = btn.getAttribute('data-sd-view-mobile');
          self.setView('mobile', cols);
          store.set('sd-cgrid-view-mobile', cols);
        });
      });

      var clear = this.querySelector('[data-sd-clear-family]');
      if (clear) {
        clear.addEventListener('click', function () {
          self.setFamily('');
        });
      }

      var reset = this.querySelector('[data-sd-reset]');
      if (reset) {
        reset.addEventListener('click', function () {
          self.material = '';
          self.family = '';
          self.apply(true);
        });
      }

      if (this.sortEl) {
        this.sortEl.addEventListener('change', function () {
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', self.sortEl.value);
          url.searchParams.delete('page');
          window.location.href = url.toString();
        });
      }
    }

    hasFamily(family) {
      if (!family) return true;
      return this.items.some(function (item) {
        return (' ' + item.getAttribute('data-family') + ' ').indexOf(' ' + family + ' ') !== -1;
      });
    }

    setFamily(family) {
      var self = this;
      this.family = clean(family);
      if (this.material && this.family) {
        var match = this.items.some(function (item) {
          var fams = ' ' + (item.getAttribute('data-family') || '') + ' ';
          return item.getAttribute('data-material') === self.material && fams.indexOf(' ' + self.family + ' ') !== -1;
        });
        if (!match) this.material = '';
      }
      this.apply(true);
    }

    restoreView() {
      var desktop = store.get('sd-cgrid-view');
      var mobile = store.get('sd-cgrid-view-mobile');
      var desktopOk = this.viewButtons.some(function (b) { return b.getAttribute('data-sd-view') === desktop; });
      var mobileOk = this.viewMobileButtons.some(function (b) { return b.getAttribute('data-sd-view-mobile') === mobile; });
      if (desktop && desktopOk) this.setView('desktop', desktop);
      if (mobile && mobileOk) this.setView('mobile', mobile);
    }

    setView(kind, cols) {
      if (!this.grid) return;
      if (kind === 'desktop') {
        this.grid.style.setProperty('--sd-cols', cols);
        this.grid.style.setProperty('--sd-tile-span', Math.min(2, parseInt(cols, 10) || 2));
        this.viewButtons.forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-sd-view') === cols ? 'true' : 'false');
        });
      } else {
        this.grid.style.setProperty('--sd-cols-mobile', cols);
        this.viewMobileButtons.forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-sd-view-mobile') === cols ? 'true' : 'false');
        });
      }
    }

    familyLabel(family) {
      var link = document.querySelector('[data-sd-family-link][data-family="' + family + '"]');
      var label = link && link.getAttribute('data-family-label');
      return label || titleCase(family);
    }

    apply(userAction) {
      var self = this;
      var shown = 0;
      var filtering = !!(this.material || this.family);

      this.items.forEach(function (item) {
        var okMaterial = !self.material || item.getAttribute('data-material') === self.material;
        var fams = ' ' + (item.getAttribute('data-family') || '') + ' ';
        var okFamily = !self.family || fams.indexOf(' ' + self.family + ' ') !== -1;
        var show = okMaterial && okFamily;
        item.hidden = !show;
        if (show) shown += 1;
      });

      this.tiles.forEach(function (tile) {
        tile.hidden = filtering;
      });

      this.materialButtons.forEach(function (btn) {
        var value = btn.getAttribute('data-sd-material') || '';
        btn.setAttribute('aria-pressed', value === self.material ? 'true' : 'false');
      });

      if (this.activeEl) {
        this.activeEl.hidden = !this.family;
        if (this.activeLabel) this.activeLabel.textContent = this.family ? this.familyLabel(this.family) : '';
      }

      if (this.countEl) {
        var total = filtering ? shown : parseInt(this.countEl.getAttribute('data-total'), 10) || shown;
        var template = total === 1 ? this.getAttribute('data-count-one') : this.getAttribute('data-count-other');
        if (template) this.countEl.textContent = template.replace('[count]', total);
      }

      if (this.emptyEl) this.emptyEl.hidden = shown > 0 || !this.items.length;

      if (this.grid && userAction) {
        this.grid.classList.remove('is-filtering');
        void this.grid.offsetWidth;
        this.grid.classList.add('is-filtering');
      }

      this.syncFamilyNav();
      if (userAction) this.syncUrl();
    }

    syncFamilyNav() {
      var self = this;
      var links = document.querySelectorAll('[data-sd-family-link]');
      Array.prototype.forEach.call(links, function (link) {
        if (!link.hasAttribute('data-in-page') && link.getAttribute('data-family') !== '') return;
        var fam = link.getAttribute('data-family');
        if (fam === '') {
          if (self.family) {
            link.removeAttribute('aria-current');
          } else if (link.hasAttribute('data-was-current')) {
            link.setAttribute('aria-current', 'page');
          }
          return;
        }
        if (fam === self.family) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    syncUrl() {
      if (!window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        if (this.material) url.searchParams.set('material', this.material);
        else url.searchParams.delete('material');
        if (this.family) url.searchParams.set('family', this.family);
        else url.searchParams.delete('family');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* ignore */ }
    }
  }

  class SdFamilyNav extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var links = Array.prototype.slice.call(this.querySelectorAll('[data-sd-family-link]'));

      links.forEach(function (link) {
        link.addEventListener('click', function (event) {
          var grid = activeGrid();
          if (!grid || typeof grid.setFamily !== 'function') return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
          var fam = link.getAttribute('data-family');

          if (fam === '') {
            if (grid.family) {
              event.preventDefault();
              grid.setFamily('');
            }
            return;
          }

          if (!link.hasAttribute('data-in-page')) return;
          if (!grid.hasFamily(clean(fam))) return;
          event.preventDefault();
          grid.setFamily(grid.family === clean(fam) ? '' : fam);
        });
      });

      var current = this.querySelector('[aria-current]');
      if (current && current.scrollIntoView && this.firstElementChild) {
        var list = this.firstElementChild;
        if (list.scrollWidth > list.clientWidth) {
          list.scrollLeft = Math.max(0, current.offsetLeft - 16);
        }
      }
    }
  }

  window.customElements.define('sd-collection-grid', SdCollectionGrid);
  if (!window.customElements.get('sd-family-nav')) window.customElements.define('sd-family-nav', SdFamilyNav);
})();

/* ==========================================================================
   Replicatie 01-10-2026: C1 carrousel (Polène), C2 kaart + load more (CAINTÈ),
   C3 materiaalrij (DeMellier). Vanilla, geen libraries.
   ========================================================================== */
(function () {
  if (!customElements.get('sd-c1-carousel')) {
    customElements.define('sd-c1-carousel', class extends HTMLElement {
      connectedCallback() {
        this.vp = this.querySelector('[data-sd-c1-viewport]');
        this.prev = this.querySelector('[data-sd-c1-prev]');
        this.next = this.querySelector('[data-sd-c1-next]');
        if (!this.vp) return;
        var self = this;
        var step = function () { var s = self.vp.querySelector('.sd-c1__slide'); return s ? s.getBoundingClientRect().width : 160; };
        if (this.prev) this.prev.addEventListener('click', function () { self.vp.scrollBy({ left: -step(), behavior: 'smooth' }); });
        if (this.next) this.next.addEventListener('click', function () { self.vp.scrollBy({ left: step(), behavior: 'smooth' }); });
        this.update = this.update.bind(this);
        this.vp.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        this.update();
      }
      update() {
        var max = this.vp.scrollWidth - this.vp.clientWidth;
        this.classList.toggle('is-static', max <= 1);
        if (this.prev) this.prev.disabled = this.vp.scrollLeft <= 1;
        if (this.next) this.next.disabled = this.vp.scrollLeft >= max - 1;
      }
    });
  }

  if (!customElements.get('sd-c3-materials')) {
    customElements.define('sd-c3-materials', class extends HTMLElement {
      connectedCallback() {
        var map = {};
        (this.dataset.map || '').split(' ').forEach(function (pair) {
          var p = pair.split(':'); if (p[0]) map[p[0]] = p[1];
        });
        this.map = map;
        var self = this;
        this.querySelectorAll('[data-material]').forEach(function (btn) {
          btn.addEventListener('click', function () { self.apply(btn.getAttribute('data-material')); });
        });
        document.addEventListener('sd:c2:appended', function () { self.apply(self.current || ''); });
      }
      apply(material) {
        this.current = material;
        this.querySelectorAll('[data-material]').forEach(function (b) {
          var on = b.getAttribute('data-material') === material;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        var map = this.map, visible = 0;
        var items = document.querySelectorAll('[data-sd-c2-item]');
        items.forEach(function (li) {
          var show = !material || (map[li.dataset.productHandle] || 'grain') === material;
          li.hidden = !show;
          if (show) visible++;
        });
        document.querySelectorAll('[data-sd-c2-empty]').forEach(function (p) { p.hidden = visible > 0 || !items.length; });
      }
    });
  }

  function bindCard(root) {
    root.querySelectorAll('[data-sd-c2-slides]').forEach(function (track) {
      if (track.dataset.bound) return;
      track.dataset.bound = '1';
      var bar = track.parentNode.querySelector('[data-sd-c2-bar]');
      if (!bar) return;
      track.addEventListener('scroll', function () {
        var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        bar.style.setProperty('--sd-c2-i', i);
      }, { passive: true });
    });
    root.querySelectorAll('[data-sd-c2-wish]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      var key = 'sd-wishlist', list = [];
      try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) {}
      var h = btn.getAttribute('data-sd-c2-wish');
      btn.setAttribute('aria-pressed', list.indexOf(h) > -1 ? 'true' : 'false');
      btn.addEventListener('click', function () {
        try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { list = []; }
        var i = list.indexOf(h);
        if (i > -1) list.splice(i, 1); else list.push(h);
        try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) {}
        btn.setAttribute('aria-pressed', i > -1 ? 'false' : 'true');
      });
    });
    root.querySelectorAll('form[data-sd-c2-add]').forEach(function (form) {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button');
        if (btn) btn.setAttribute('aria-busy', 'true');
        var item;
        fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(form.elements.id.value), quantity: 1 }] })
        }).then(function (r) { if (!r.ok) throw r; return r.json(); })
          .then(function (res) { item = res.items ? res.items[0] : res; return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(function (cart) {
            if (btn) btn.removeAttribute('aria-busy');
            document.dispatchEvent(new CustomEvent('sd:cart:updated', { detail: { item_count: cart.item_count, cart: cart } }));
            var evt = new CustomEvent('sd:cart:added', { cancelable: true, detail: { item: item, cart: cart, productHandle: form.closest('[data-product-handle]') ? form.closest('[data-product-handle]').dataset.productHandle : '' } });
            if (document.dispatchEvent(evt)) window.location.href = '/cart';
          })
          .catch(function () { if (btn) btn.removeAttribute('aria-busy'); form.submit(); });
      });
    });
  }

  if (!customElements.get('sd-c2-grid')) {
    customElements.define('sd-c2-grid', class extends HTMLElement {
      connectedCallback() {
        bindCard(this);
        var self = this;
        this.addEventListener('click', function (e) {
          var a = e.target.closest('[data-sd-c2-next]');
          if (!a) return;
          e.preventDefault();
          if (a.getAttribute('aria-busy') === 'true') return;
          a.setAttribute('aria-busy', 'true');
          var url = a.getAttribute('href');
          url += (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(a.dataset.section);
          fetch(url).then(function (r) { return r.text(); }).then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var list = self.querySelector('[data-sd-c2-list]');
            doc.querySelectorAll('[data-sd-c2-item]').forEach(function (li) { list.appendChild(document.importNode(li, true)); });
            var more = self.querySelector('[data-sd-c2-more]');
            var nextMore = doc.querySelector('[data-sd-c2-more]');
            if (more) { if (nextMore) more.replaceWith(document.importNode(nextMore, true)); else more.remove(); }
            bindCard(self);
            document.dispatchEvent(new CustomEvent('sd:c2:appended'));
          }).catch(function () { window.location.href = a.getAttribute('href'); });
        });
      }
    });
  }
})();
